<script lang="ts" setup>
import { ref, computed, onMounted } from 'vue';
import { useRoute } from 'vue-router';
import { useCompanyStore } from '@Stores/company.store';
import { useLocationStore } from '@Stores/location.store';
import useCompanyImageBaseUrl from '@Src/use/useCompanyImageBaseUrl';
import { ShowcaseLayout } from '@Shared';
import { getFraudEducationTaskApi, completeFraudEducationTaskApi } from '@Api/fraudEducation.api';
import type { FraudEducationTask } from '@Api/fraudEducation.api';

const route = useRoute();
const companyImageBaseUrl = useCompanyImageBaseUrl();
const companyStore = useCompanyStore();
const locationStore = useLocationStore();

const task = ref<FraudEducationTask | null>(null);
const isLoading = ref(true);
const isCompleting = ref(false);
const isCompleted = ref(false);
const errorMessage = ref<string | null>(null);

const youtubeEmbedUrl = computed(() => {
    if (!task.value?.youtubeUrl) return null;
    const url = task.value.youtubeUrl;
    const match = url.match(/(?:youtube\.com\/watch\?v=|youtu\.be\/|youtube\.com\/embed\/)([a-zA-Z0-9_-]{11})/);
    const videoId = match?.[1];
    return videoId ? `https://www.youtube.com/embed/${videoId}?rel=0` : null;
});

const showCompleteButton = computed(() =>
    task.value && !isCompleted.value && task.value.status !== 'completed'
);

onMounted(async () => {
    const taskId = route.params.taskId as string;
    if (!taskId) {
        errorMessage.value = 'Missing task information.';
        isLoading.value = false;
        return;
    }
    try {
        task.value = await getFraudEducationTaskApi(taskId);
        if (task.value.status === 'completed') {
            isCompleted.value = true;
        }
    } catch (err) {
        console.error('Failed to load fraud education task:', err);
        errorMessage.value = 'Failed to load fraud education content.';
    } finally {
        isLoading.value = false;
    }
});

const handleComplete = async () => {
    if (!task.value || isCompleting.value) return;
    isCompleting.value = true;
    try {
        await completeFraudEducationTaskApi(task.value.taskId);
        isCompleted.value = true;
    } catch (err) {
        console.error('Failed to complete fraud education task:', err);
        errorMessage.value = 'Failed to mark task as complete. Please try again.';
    } finally {
        isCompleting.value = false;
    }
};
</script>

<template>
    <showcase-layout
        :company="companyStore.company"
        :brand="companyStore.brand"
        :location="locationStore.location"
        :company-image-base-url="companyImageBaseUrl"
        show-feature-logo
        show-powered-by
        :show-back-button="false"
    >
        <div class="fraud-education">
            <app-page-loader v-if="isLoading" />

            <template v-else-if="errorMessage && !task">
                <p class="fraud-education__error" role="alert">{{ errorMessage }}</p>
            </template>

            <div v-else-if="task" class="fraud-education__content">
                <p v-if="task.text" class="fraud-education__text">{{ task.text }}</p>

                <div v-if="youtubeEmbedUrl" class="fraud-education__video">
                    <iframe
                        :src="youtubeEmbedUrl"
                        title="Fraud prevention education video"
                        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                        allowfullscreen
                    ></iframe>
                </div>

                <div class="fraud-education__frs-link">
                    <app-link
                        type="external"
                        link-url="https://reportafraud.org"
                        data-id="fraud-education-frs-reporting"
                    >
                        FRS Reporting
                    </app-link>
                </div>

                <div v-if="isCompleted" class="fraud-education__completed">
                    <p>You have completed this task. Thank you for reviewing the fraud prevention information.</p>
                </div>

                <app-button
                    v-if="showCompleteButton"
                    expanded
                    size="large"
                    :is-loading="isCompleting"
                    data-id="fraud-education-mark-complete"
                    @onClick="handleComplete"
                >
                    Mark as Complete
                </app-button>

                <p v-if="errorMessage && task" class="fraud-education__error-inline" role="alert">{{ errorMessage }}</p>
            </div>
        </div>
    </showcase-layout>
</template>

<style lang="scss" scoped>
@use "certifid-v2-client-hub-styles" as *;

.fraud-education {
    color: $black-high;

    &__text {
        font-size: 1rem;
        line-height: 1.6;
        margin-bottom: 1.5rem;
        text-align: left;
    }

    &__video {
        position: relative;
        width: 100%;
        padding-bottom: 56.25%; // 16:9 aspect ratio
        margin-bottom: 1.5rem;
        border-radius: 8px;
        overflow: hidden;
        background: #000;

        iframe {
            position: absolute;
            top: 0;
            left: 0;
            width: 100%;
            height: 100%;
            border: none;
        }
    }

    &__frs-link {
        text-align: center;
        margin-bottom: 1rem;
    }

    &__completed {
        padding: 1rem;
        margin-bottom: 1rem;
        border-radius: 6px;
        background: #d4edda;
        color: #155724;
        font-weight: 600;
        font-size: 0.9375rem;
        line-height: 1.5;
    }

    &__error {
        padding: 2rem;
        font-size: 1rem;
        color: #d32f2f;
    }

    &__error-inline {
        margin-top: 0.75rem;
        font-size: 0.875rem;
        color: #d32f2f;
    }
}
</style>
